'use client';

import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useGeo } from '../../../GeoProvider/hook';
import { SingleSelectDropdown } from '../../../OffersFilter/SingleSelectDropdown';
import { useFilterEvents } from '../../../OffersFilter/useFilterEvents';
import { useFilterLocationOptions } from '../store/hooks';
import { useOffersFilterStore } from '../store/useOffersFilterStore';

export const LocationSelect = ({
  isStickyBar = false,
  scrollBackToMainBar,
  actionSection,
  pageIdentifier,
}: {
  isStickyBar?: boolean;
  scrollBackToMainBar?: () => void;
  actionSection: string;
  pageIdentifier: string;
}) => {
  const locationOptions = useFilterLocationOptions();
  const { regionCode, setRegionCode } = useGeo();
  const { handleUpdateFilterFormState, handleApplyFilters, appliedFilters } = useOffersFilterStore(
    ({ handleUpdateFilterFormState, handleApplyFilters, appliedFilters }) => ({
      handleUpdateFilterFormState,
      handleApplyFilters,
      appliedFilters,
    }),
    shallow,
  );
  const { updateFiltersEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
    additionalAttributes: {},
  });

  const handleChange = useCallback(
    (selectedLocation: string) => {
      if (scrollBackToMainBar) scrollBackToMainBar();
      setTimeout(
        () => {
          handleUpdateFilterFormState({ selectedLocation });
          setRegionCode(selectedLocation as StateAbbreviation);
          const newState = handleApplyFilters();
          if (!newState) return;
          updateFiltersEvent({
            additional_attributes: {
              location: selectedLocation,
              product_types: newState.appliedFilters.selectedProductTypes,
            },
          });
        },
        scrollBackToMainBar ? 100 : 0,
      );
    },
    [
      handleUpdateFilterFormState,
      handleApplyFilters,
      updateFiltersEvent,
      scrollBackToMainBar,
      setRegionCode,
    ],
  );

  const selectedLocation = appliedFilters?.selectedLocation ?? regionCode;

  return (
    <div style={{ minWidth: 200 }}>
      <SingleSelectDropdown
        isStickyBar={isStickyBar}
        label="Location"
        options={locationOptions}
        selectedOption={selectedLocation}
        onChange={handleChange}
        useStore={useOffersFilterStore}
        placeholder="Location"
        scrollBackToMainBar={scrollBackToMainBar}
      />
    </div>
  );
};
